import { Suspense, lazy, useEffect } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";
// layouts
import DashboardLayout from "../layouts/dashboard";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";
// guards
import GuestGuard from "../guards/GuestGuard";
import AdminGuard from "../guards/AdminGuard";
// components
import LoadingScreen from "../components/LoadingScreen";
// hooks
import useAuth from "../hooks/useAuth";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { selectSnackbar } from "../features/snackbarSlice";
import RestrictedOnlyAdmin from "../guards/RestrictedOnlyAdmin";

// import Home from "src/pages/home/Home";

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense
      fallback={<LoadingScreen isDashboard={pathname.includes("/dashboard")} />}
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router({ ownerTemplate }) {
  const { user } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const snackbar = useSelector(selectSnackbar);

  useEffect(() => {
    // snackbar to display the error and success messages
    if (snackbar && snackbar.message !== "" && snackbar.variant !== "") {
      let variant = snackbar?.variant;
      enqueueSnackbar(snackbar?.message, { variant });
    }
  }, [snackbar, enqueueSnackbar]);

  return useRoutes([
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: "register",
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: "login-unprotected", element: <Login /> },
        { path: "register-unprotected", element: <Register /> },
        { path: "reset-password", element: <ResetPassword /> },
        { path: "verify", element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: "dashboard",
      element: (
        <AdminGuard>
          <DashboardLayout />
        </AdminGuard>
      ),
      children: [
        {
          element: (
            <Navigate
              to={
                user &&
                user.id !== "" &&
                (user.role === "super-admin" ||
                  user.role === "Admin" ||
                  user.role === "Secretary" ||
                  user.role === "SalePerson" ||
                  user.role === "client")
                  ? "/dashboard/app1"
                  : "" /* PATH_AFTER_LOGIN */
              }
              replace
            />
          ),
          index: true,
        },
        {
          path: "app1",
          element: (
            <AdminGuard>
              <GeneralApp1 />
            </AdminGuard>
          ),
        },

        {
          path: "payment",
          element: (
            <AdminGuard>
              <PaymentIndex />
            </AdminGuard>
          ),
        },

        {
          path: "report",
          element: (
            <AdminGuard>
              <ReportIndex />
            </AdminGuard>
          ),
        },

        {
          path: "company",
          element: (
            <AdminGuard>
              <CompanyDetail />
            </AdminGuard>
          ),
        },
        {
          path: "client",
          element: (
            <AdminGuard>
              <ClientDetail />
            </AdminGuard>
          ),
        },

        {
          path: "my-account-admin",
          element: (
            <AdminGuard>
              <UserAccount />
            </AdminGuard>
          ),
        },

        {
          path: "booking-vehicles",
          element: (
            <AdminGuard>
              <BookingVehiclesList />
            </AdminGuard>
          ),
        },

        {
          path: "vehicles",
          element: (
            <AdminGuard>
              <VehicleDetail />
            </AdminGuard>
          ),
        },

        {
          path: "contracts",
          element: (
            <AdminGuard>
              <ContractDetail />
            </AdminGuard>
          ),
        },

        {
          path: "administrators",
          element: (
            <AdminGuard>
              <AdminDetail />
            </AdminGuard>
          ),
        },
        {
          path: "superadministrators",
          element: (
            <RestrictedOnlyAdmin>
              <SuperAdminDetail />
            </RestrictedOnlyAdmin>
          ),
        },
      ],
    },

    // Main Routes
    {
      path: "*",
      element: <LogoOnlyLayout />,
      children: [
        /* {
          path: "home",
          element: <Home />,
        }, */
        {
          path: "privacy-policy",
          element: <PrivacyPolicy />,
        },
        { path: "500", element: <Page500 /> },
        { path: "404", element: <NotFound /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: "/",
      element: (
        <Suspense fallback={<></>}>
          <MainLayout />
        </Suspense>
      ),
      children: [
        {
          element: <HomeCarRental />,
          index: true,
        },
        {
          path: "service-detail",
          element: <EcommerceProductDetailsServices />,
        },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS
const MainLayout = Loadable(lazy(() => import("../layouts/main")));
// Authentication
const Login = Loadable(lazy(() => import("../pages/auth/Login")));
const Register = Loadable(lazy(() => import("../pages/auth/Register")));
const PrivacyPolicy = Loadable(lazy(() => import("../pages/PrivacyPolicy")));
const ResetPassword = Loadable(
  lazy(() => import("../pages/auth/ResetPassword"))
);
const VerifyCode = Loadable(lazy(() => import("../pages/auth/VerifyCode")));
// Dashboard
/* const GeneralApp = Loadable(
  lazy(() => import("../pages/dashboard/GeneralApp"))
); */
const GeneralApp1 = Loadable(
  lazy(() => import("../pages/dashboard/GeneralApp1"))
);

const PaymentIndex = Loadable(
  lazy(() => import("../pages/dashboard/payment/PaymentIndex"))
);

const ReportIndex = Loadable(
  lazy(() => import("../pages/dashboard/report/ReportIndex"))
);

const CompanyDetail = Loadable(
  lazy(() => import("../pages/dashboard/manage-company/CompanyDetail"))
);
const ClientDetail = Loadable(
  lazy(() => import("../pages/dashboard/manage-client/ClientDetail"))
);

const AdminDetail = Loadable(
  lazy(() => import("../pages/dashboard/manage-admin/AdminDetail"))
);
const SuperAdminDetail = Loadable(
  lazy(() => import("../pages/dashboard/manage-super-admin/SuperAdminDetail"))
);
const UserAccount = Loadable(
  lazy(() => import("../pages/dashboard/UserAccount"))
);
const Page500 = Loadable(lazy(() => import("../pages/Page500")));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));

// car rental atish project
const HomeCarRental = Loadable(
  lazy(() => import("../pages/car-rental-atish/car-rental/HomeCarRental"))
);
const EcommerceProductDetailsServices = Loadable(
  lazy(() =>
    import("../pages/dashboard/ecommerce/EcommerceProductDetailsServices")
  )
);
const VehicleDetail = Loadable(
  lazy(() =>
    import("src/pages/car-rental-atish/car-rental/vehicle-detail/VehicleDetail")
  )
);
const ContractDetail = Loadable(
  lazy(() =>
    import(
      "src/pages/car-rental-atish/car-rental/contract-detail/ContractDetail"
    )
  )
);
const BookingVehiclesList = Loadable(
  lazy(() =>
    import("src/pages/car-rental-atish/car-rental/booking/BookingVehiclesList")
  )
);
